import type { ComponentType } from 'react';
import { Route } from 'react-router-dom';
import { EmployeeQuiz, FreeWorkQuiz, HireQuiz, LatePayerQuiz, MaterialQuiz, QuizHub, QuoteQuiz, RateQuiz, VehicleQuiz } from '#route-pages';
import type { TradeCode } from '@/utils/rateQuiz/countries';
import { quizPages, type QuizKind } from '@/utils/rateQuiz/routes';
import { getRouteByKey, isRouteAvailable, type SiteLanguage } from '@/utils/routing';

type QuizPage = ComponentType<{ trade?: TradeCode; view?: 'quiz' | 'result' }>;

const PAGES: Record<QuizKind, QuizPage> = {
  rateQuiz: RateQuiz as QuizPage,
  employeeQuiz: EmployeeQuiz as QuizPage,
  vehicleQuiz: VehicleQuiz as QuizPage,
  quoteQuiz: QuoteQuiz as QuizPage,
  latePayerQuiz: LatePayerQuiz as QuizPage,
  freeWorkQuiz: FreeWorkQuiz as QuizPage,
  hireQuiz: HireQuiz as QuizPage,
  materialQuiz: MaterialQuiz as QuizPage,
};

/**
 * <Route> elements for every quiz page of a site (hub, trade pages and their
 * result pages), generated from the slugs in routing.ts so a new site cannot
 * miss one. The Danish routes are still written out by hand in App.tsx.
 */
export function quizRouteElements(lang: SiteLanguage) {
  const pages = quizPages(lang).map(({ path, quiz, trade, view }) => {
    const Page = PAGES[quiz];
    return <Route key={path} path={path} element={<Page trade={trade} view={view} />} />;
  });
  // The hub at /quiz/ is shared with the Danish routes; a site with its own hub path (Czech and Slovak /kviz/) gets it here.
  const hub = isRouteAvailable('quizHub', lang) ? getRouteByKey('quizHub', lang) : '/quiz/';
  return hub === '/quiz/' ? pages : [<Route key={hub} path={hub} element={<QuizHub />} />, ...pages];
}
